"use client"

import { useSearchParams } from "next/navigation"
import { EmailAddressSuggestion } from "@/schema"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"

import { useEmailSendingIntegration } from "@/hooks/api/email_integrations"
import { useFetchCurrentUser } from "@/hooks/api/users/queries"
import { BookIdProvider, useBookId } from "@/hooks/use-book-id"
import { useLoadingRouter } from "@/hooks/use-loading-router"
import { EmailDialog } from "@/components/integrations"

import { ComposeEmail } from "./compose"

interface EmailDialogProps {
  toEmails?: EmailAddressSuggestion[] | null
  ccEmails?: EmailAddressSuggestion[] | null
  bccEmails?: EmailAddressSuggestion[] | null
  recommendedEmails?: EmailAddressSuggestion[] | null
  householdId?: string | null
}

export function useEmailDialog({
  toEmails = null,
  ccEmails = null,
  bccEmails = null,
  recommendedEmails = null,
  householdId = null,
}: EmailDialogProps = {}) {
  const { openDialog, closeDialog } = useGlobalDialogStore()
  const { sendingIntegrations, isLoading } = useEmailSendingIntegration()
  const hasSendableEmailIntegration = sendingIntegrations.length > 0
  // The composer's requests need a book. Outside a book route (company-level
  // pages such as custodian setup) fall back to the user's default book.
  const routeBookId = useBookId()
  const { data: currentUser } = useFetchCurrentUser()
  const dialogBookId = routeBookId ?? currentUser?.default_book?.id ?? currentUser?.books?.[0]?.id

  const searchParams = useSearchParams()
  const { replace } = useLoadingRouter()
  const queryClient = useQueryClient()

  return {
    canSendEmail: hasSendableEmailIntegration,
    isLoadingSendAccess: isLoading,
    open: (seed?: { syncboxDraftId?: string; replyToSyncboxMessageId?: string; onSent?: () => void }) => {
      const syncboxDraftId = seed?.syncboxDraftId
      const replyToSyncboxMessageId = seed?.replyToSyncboxMessageId
      const onSent = seed?.onSent

      if (syncboxDraftId) {
        queryClient.removeQueries({ queryKey: ["syncbox_email_draft", syncboxDraftId] })
      }

      const closeCallback = () => {
        const params = new URLSearchParams(searchParams.toString())
        params.delete("dialog")
        params.delete("id")
        replace(`?${params.toString()}`)

        if (syncboxDraftId) {
          queryClient.removeQueries({ queryKey: ["syncbox_email_draft", syncboxDraftId] })
        }
      }

      openDialog({
        variant: "custom",
        content: (
          <div className="w-full max-w-(--breakpoint-sm)">
            {isLoading && (
              <div className="flex h-[32rem] items-center justify-center">
                <Loader2 className="text-muted-foreground size-6 animate-spin" />
              </div>
            )}
            {!isLoading && !hasSendableEmailIntegration && <EmailDialog />}
            {!isLoading && hasSendableEmailIntegration && (
              <BookIdProvider bookId={dialogBookId}>
                <ComposeEmail
                  syncboxDraftId={syncboxDraftId}
                  replyToSyncboxMessageId={replyToSyncboxMessageId}
                  toEmails={toEmails ?? []}
                  ccEmails={ccEmails ?? []}
                  bccEmails={bccEmails ?? []}
                  recommendedEmails={recommendedEmails ?? []}
                  householdId={householdId ?? undefined}
                  onSent={
                    onSent
                      ? () => {
                          onSent()
                          closeDialog()
                        }
                      : undefined
                  }
                />
              </BookIdProvider>
            )}
          </div>
        ),
        dialogContentClasses: "max-w-(--breakpoint-sm)",
        closeCallback,
      })

      const params = new URLSearchParams(searchParams.toString())
      params.set("dialog", "email")
      if (syncboxDraftId) {
        params.set("id", syncboxDraftId)
      }
      replace(`?${params.toString()}`)
    },
    close: () => {
      closeDialog()
    },
  }
}
